import { useSignalLayer } from '../../hooks/useSignalLayer'
import { GanttTask, DisplayRow } from '../../types/gantt.types'
import { SignalDots, SignalDot } from '@indipia/ui-core'
import { SIGNAL_SLOTS, slotItem, signalCtx, SIGNAL_COLOR_LABEL, type SignalTaskContext } from '../../constants/signalView'
import { SIGNAL_SLOT_LABEL, signalPresentation } from '@indipia/shared'
// 막대 옆 신호 점 3개(반장·발주·작업)

export type GanttSignalDotsProps = {
  sigOn: boolean
  l: number
  signal: ReturnType<typeof useSignalLayer>
  task: GanttTask
  startOff: number
  span: number
  row: DisplayRow
  barIdx: number
  getTaskSL: (task: GanttTask) => { s: number; l: number; rawL: number; }
  totalDays: number
  DW: number
  barTop: number
  /** 점 title 문구 조립에 쓰는 공종 값(착공·종료일·반장 이름·순위·거래처) */
  signalTaskCtx: (taskId: number) => SignalTaskContext
}

export function GanttSignalDots({
  sigOn, l, signal, task, startOff, span, row, barIdx, getTaskSL, totalDays, DW, barTop, signalTaskCtx,
}: GanttSignalDotsProps) {
  return (
    <>
      {/* 신호 점 3개(반장·발주·작업) — 막대 오른쪽(자리가 없으면 왼쪽·막대 안). 누르면 사유 팝오버.
          서버 신호가 없거나 조회가 실패하면 점 대신 오류 표시(폴백 없음). */}
      {sigOn && l > 0 && !signal.signalsQ.isLoading && (() => {
        const ts = signal.byTask.get(task.taskId)
        const barEnd = startOff + span
        const estWidth = 52
        const nextBar = row.bars[barIdx + 1]
        const prevBar = row.bars[barIdx - 1]
        const rightLimitPx = (nextBar ? getTaskSL(nextBar).s : totalDays) * DW
        const leftLimitPx  = prevBar ? (getTaskSL(prevBar).s + getTaskSL(prevBar).l) * DW : 0
        const onRight = rightLimitPx - barEnd * DW >= estWidth
        const onLeft  = !onRight && startOff * DW - leftLimitPx >= estWidth
        const place: React.CSSProperties = onRight
          ? { left: barEnd * DW + 4 }
          : onLeft
            ? { left: startOff * DW - 4, transform: 'translateX(-100%)' }
            : { left: Math.max(startOff * DW + 2, barEnd * DW - estWidth - 2) }
        const failed = signal.signalsQ.isError || !ts
        // 셋 다 흰색·빗금(대표색 없음)이어도 캡슐을 그린다 — 막 생성한 간트에서도 공종마다 신호 상태가 보이게
        // 작업 신호가 파랑(작업 완료)이면 캡슐 바탕을 파랑으로 채우고, 작업 점만 흰 테두리로 구분한다(941 S15-2)
        const workBlue = !failed && slotItem(ts, 'WORK')?.colorCode === 'BLUE'
        const taskCtx = failed ? {} : signalTaskCtx(task.taskId)
        // 점별 안내(title) — '슬롯 · 상태명 · 한 줄 문구'. 문구를 만들 수 없는 코드는 임의 문구 없이 표시 불가로 안내
        const dotTitle = (slot: typeof SIGNAL_SLOTS[number]) => {
          const it = slotItem(ts, slot)
          if (!it) return `${SIGNAL_SLOT_LABEL[slot]} · 신호 없음`
          const pres = signalPresentation(it.reasonCode, signalCtx(it, taskCtx))
          return pres ? `${SIGNAL_SLOT_LABEL[slot]} · ${pres.name} · ${pres.line}` : `${SIGNAL_SLOT_LABEL[slot]} · 표시할 수 없는 신호 상태`
        }
        return (
          <div style={{ position: 'absolute', top: barTop, height: 20, display: 'flex', alignItems: 'center', zIndex: 3, ...place }}>
            {failed ? (
              <span
                role="img"
                aria-label="신호 오류"
                title="신호 정보를 불러오지 못했어요"
                style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--status-danger-600)', color: 'var(--shell-surface)', fontSize: 10, fontWeight: 700, lineHeight: '14px', textAlign: 'center' }}
              >!</span>
            ) : (
              <button
                type="button"
                aria-label={`${row.workTypeName} 신호 사유 보기`}
                title="신호등 사유 보기"
                aria-expanded={signal.popover?.taskId === task.taskId}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => {
                  e.stopPropagation()
                  if (signal.popover?.taskId === task.taskId) { signal.closePopover(); return }
                  const r = e.currentTarget.getBoundingClientRect()
                  signal.setPopover({ taskId: task.taskId, anchor: { x: r.left, y: r.bottom + 6 } })
                }}
                style={{ display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 6px', borderRadius: 10, border: `var(--border-width-hairline) solid ${workBlue ? 'var(--signal-blue)' : 'var(--shell-border)'}`, background: workBlue ? 'var(--signal-blue)' : 'var(--shell-surface)', cursor: 'pointer' }}
              >
                <SignalDots>
                  {SIGNAL_SLOTS.map(slot => {
                    const it = slotItem(ts, slot)
                    // 파랑 캡슐 위에서 작업 점(3번째)만 흰 테두리 — 토큰 --gray-0(#FFFFFF, D-08 §6.15 점 테두리 흰색)
                    const whiteRing = workBlue && slot === 'WORK' ? { border: 'var(--border-width-base) solid var(--gray-0)' } : undefined
                    return it
                      ? <SignalDot key={slot} color={it.colorCode} title={dotTitle(slot)} style={whiteRing} label={`${SIGNAL_SLOT_LABEL[slot]} 신호 ${SIGNAL_COLOR_LABEL[it.colorCode]}`} />
                      : <SignalDot key={slot} color="NONE" title={dotTitle(slot)} label={`${SIGNAL_SLOT_LABEL[slot]} 신호 없음`} />
                  })}
                </SignalDots>
              </button>
            )}
          </div>
        )
      })()}
    </>
  )
}
